import { CaretDown } from "@phosphor-icons/react";
import { Button, Text } from "@radix-ui/themes";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { GraphCitationDialog, graphCitationLabel } from "./GraphCitationDialog";

import {
  citationCardClass,
  sourceBadgeClass,
} from "../../../components/ask-puntt-sources";
import {
  KnowledgeRuleCitationDialog,
  knowledgeRuleCitationLabel,
} from "../../../components/KnowledgeRuleCitationDialog";
import { isPunttGuest } from "../../../utils/auth";
import {
  type TicketMessageCitation,
  dedupeGraphCitations,
  hasConflictingVerdicts,
  isKnowledgeRuleCitation,
  ruleCitationKey,
} from "../utils/commentSourceUtils";

/**
 * Cards shown before "See all": about what the rail holds without scrolling.
 * why: a claims answer cites every rule it states, and on epic chat 6abd29ee
 * that was 32 cards in one column.
 */
export const VISIBLE_CARDS = 6;

/**
 * Numbered claim cards for Knowledge Database rule citations on an assistant
 * message. Used as a right rail on plain chats, a Chat Details section, or a
 * collapsible block atop the 3-pane context column.
 */
export function CitedClaimsPanel({
  citations,
  selectedRuleId,
  onSelect,
  variant = "rail",
  testIdPrefix = "cited-claims",
  panelOpen: panelOpenProp,
  onPanelOpenChange,
  title,
  hideTitle = false,
  firstNumber = 1,
  numbers,
}: {
  /** The section's name (and accessible name); "Cited claims" when unset. */
  title?: string;
  /** Drop the visible heading when the container already names the list. */
  hideTitle?: boolean;
  /** The first card's number, so a second list continues the count. */
  firstNumber?: number;
  /** The thread's numbering (`threadCitationNumbers`): a card carries its
   * source's number there, matching the prose, and the cards sort by it. */
  numbers?: ReadonlyMap<string, number>;
  citations: readonly TicketMessageCitation[];
  selectedRuleId?: string | null;
  onSelect?: (ruleId: string) => void;
  /** `rail` = standalone right panel chrome; `section` = embedded block. */
  variant?: "rail" | "section";
  testIdPrefix?: string;
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation("ui");
  const numberOf = (
    citation: Extract<TicketMessageCitation, { ruleId: string }>,
  ): number | undefined => numbers?.get(ruleCitationKey(citation));
  const ruleCitations = citations.filter(isKnowledgeRuleCitation);
  if (numbers) {
    ruleCitations.sort(
      (a, b) =>
        (numberOf(a) ?? Number.MAX_SAFE_INTEGER) -
        (numberOf(b) ?? Number.MAX_SAFE_INTEGER),
    );
  }
  // The graph's own contribution: which rules relate, and how. A rule chip
  // answers "this rule backs the answer" and structurally cannot say that two
  // of them contradict each other or share one substantiation condition.
  const graphCitations = dedupeGraphCitations(citations);
  const [panelOpenInternal, setPanelOpenInternal] = useState(true);
  const panelOpen = panelOpenProp ?? panelOpenInternal;
  const setPanelOpen = (next: boolean) => {
    onPanelOpenChange?.(next);
    if (panelOpenProp === undefined) setPanelOpenInternal(next);
  };
  const [dialogRuleId, setDialogRuleId] = useState<string | null>(null);
  const selectedCardRef = useRef<HTMLButtonElement | null>(null);
  const [showAll, setShowAll] = useState(false);
  const listId = useId();
  const selectedIndex = ruleCitations.findIndex(
    (citation) => citation.ruleId === selectedRuleId,
  );

  // A card the reader picked from the prose opens the list when it sits past
  // the first few, or the selection would point at nothing.
  //
  // why `selectedRuleId` is a dep and not just the index it resolves to: this
  // is ONE panel instance that is handed a DIFFERENT answer's citations as the
  // reader scrolls back through the thread (#5848) — the citations change
  // under it, the component never remounts. Keyed on the derived index alone,
  // a reader who had collapsed the long list and then clicked the [8] marker
  // of another answer saw nothing happen at all: that selection also resolves
  // to index 7, so the dep never changed, the list stayed cut at
  // VISIBLE_CARDS, and the card the marker points at never mounted (which also
  // left the scroll effect below with a null ref). The selection itself is
  // what "the reader picked a card" means, so it is what this watches.
  useEffect(() => {
    if (selectedIndex >= VISIBLE_CARDS) setShowAll(true);
  }, [selectedRuleId, selectedIndex]);

  useEffect(() => {
    if (!selectedRuleId) return;
    const node = selectedCardRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
    // why showAll: a card past the fold mounts only once the list opens.
  }, [selectedRuleId, showAll]);

  if (ruleCitations.length === 0 && graphCitations.length === 0) return null;

  const fallbackLabel = t("citedClaimsPanel.citedClaim");
  const header = title ?? t("citedClaimsPanel.title");
  // why: the quote is the Knowledge Database source text, which the dialog's
  // `allowSourcePreview` already withholds from share-link guests.
  const guest = isPunttGuest();

  const graphBlock =
    graphCitations.length === 0 ? null : (
      <div className="mt-3" data-testid={`${testIdPrefix}-graph`}>
        <Text
          as="p"
          size="1"
          weight="medium"
          className="mb-1.5 text-puntt-neutral-gray-10"
        >
          {t("citedClaimsPanel.graphTitle")}
        </Text>
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {graphCitations.map((citation) => {
            const conflicted = hasConflictingVerdicts(citation);
            return (
              <li key={citation.graphNodeId}>
                <GraphCitationDialog
                  citation={citation}
                  trigger={
                    <button
                      type="button"
                      data-testid={`${testIdPrefix}-graph-chip-${citation.graphNodeId}`}
                      aria-label={t("citedClaimsPanel.graphChipAriaLabel", {
                        statement: graphCitationLabel(
                          citation,
                          t("citedClaimsPanel.citedClaim"),
                        ),
                      })}
                      className={`w-full rounded-lg border px-3 py-2 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9 ${
                        conflicted
                          ? "border-puntt-warning-9 bg-puntt-warning-2 text-puntt-neutral-gray-12"
                          : "border-puntt-neutral-gray-5 bg-base-white text-puntt-neutral-gray-11 hover:border-puntt-neutral-gray-7"
                      }`}
                    >
                      {graphCitationLabel(
                        citation,
                        t("citedClaimsPanel.citedClaim"),
                      )}
                    </button>
                  }
                />
              </li>
            );
          })}
        </ul>
      </div>
    );

  // The numbers stay the full list's: the prose's markers point at them.
  const shownCitations = showAll
    ? ruleCitations
    : ruleCitations.slice(0, VISIBLE_CARDS);
  const seeAll =
    ruleCitations.length > VISIBLE_CARDS ? (
      <Button
        type="button"
        size="1"
        variant="ghost"
        color="gray"
        onClick={() => setShowAll(!showAll)}
        aria-expanded={showAll}
        aria-controls={listId}
        data-testid={`${testIdPrefix}-see-all`}
        className="self-start"
      >
        {showAll
          ? t("citedClaimsPanel.seeFewer")
          : t("citedClaimsPanel.seeAll", { count: ruleCitations.length })}
      </Button>
    ) : null;

  const cards = (
    <ul
      id={listId}
      className="m-0 flex list-none flex-col gap-2 p-0"
      data-testid={`${testIdPrefix}-list`}
    >
      {shownCitations.map((citation, index) => {
        const n = numberOf(citation) ?? firstNumber + index;
        const cardTitle = knowledgeRuleCitationLabel(citation, fallbackLabel, {
          allowQuote: !guest,
        });
        const quote = citation.quote?.trim();
        const showQuote =
          !guest && !!quote && quote.toLowerCase() !== cardTitle.toLowerCase();
        const selected = selectedRuleId === citation.ruleId;
        const dialogOpen = dialogRuleId === citation.ruleId;

        return (
          <li key={citation.ruleId}>
            <KnowledgeRuleCitationDialog
              citation={citation}
              open={dialogOpen}
              onOpenChange={(open) =>
                setDialogRuleId(open ? citation.ruleId : null)
              }
              showKnowledgeDatabaseLink={!guest}
              allowSourcePreview={!guest}
            />
            <button
              type="button"
              ref={selected ? selectedCardRef : undefined}
              data-testid={`${testIdPrefix}-card-${citation.ruleId}`}
              data-selected={selected ? "true" : "false"}
              aria-label={t("citedClaimsPanel.cardAriaLabel", {
                n,
                statement: cardTitle,
              })}
              aria-pressed={selected}
              onClick={() => {
                onSelect?.(citation.ruleId);
                setDialogRuleId(citation.ruleId);
              }}
              className={citationCardClass(selected)}
            >
              <div className="flex items-start gap-2">
                <span
                  className={`${sourceBadgeClass(selected)} shrink-0`}
                  aria-hidden="true"
                  data-testid={`${testIdPrefix}-card-number-${citation.ruleId}`}
                >
                  {n}
                </span>
                <Text
                  as="span"
                  size="2"
                  weight="medium"
                  className="min-w-0 flex-1 text-puntt-neutral-gray-12"
                  data-testid={`${testIdPrefix}-card-title-${citation.ruleId}`}
                >
                  {cardTitle}
                </Text>
              </div>
              {showQuote ? (
                <Text
                  as="span"
                  size="1"
                  className="pl-7 text-puntt-neutral-gray-10"
                  data-testid={`${testIdPrefix}-card-quote-${citation.ruleId}`}
                >
                  {quote}
                </Text>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );

  if (variant === "section") {
    return (
      <section
        className="flex flex-col gap-2"
        aria-label={header}
        data-testid={`${testIdPrefix}-section`}
      >
        {hideTitle ? null : (
          <h3
            className="text-[13px] font-bold text-puntt-neutral-gray-12"
            data-testid={`${testIdPrefix}-section-title`}
          >
            {header}
          </h3>
        )}
        {cards}
        {seeAll}
        {graphBlock}
      </section>
    );
  }

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${panelOpen ? "h-full flex-1" : "shrink-0"}`}
      aria-label={header}
      data-testid={`${testIdPrefix}-panel`}
      data-panel-expanded={panelOpen ? "true" : "false"}
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-puntt-neutral-gray-5 px-4 py-3">
        <button
          type="button"
          onClick={() => setPanelOpen(!panelOpen)}
          aria-expanded={panelOpen}
          aria-label={t("citedClaimsPanel.toggle")}
          data-testid={`${testIdPrefix}-toggle`}
          className="flex min-w-0 flex-1 items-center gap-2 bg-base-white text-left"
        >
          <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
            {header}
          </h2>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${panelOpen ? "" : "-rotate-90"}`}
          />
        </button>
      </div>
      {panelOpen ? (
        <div
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain p-4 [scrollbar-width:thin]"
          data-testid={`${testIdPrefix}-scroll`}
        >
          {cards}
          {seeAll}
          {graphBlock}
        </div>
      ) : null}
    </section>
  );
}
